import { AlertTriangle, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@project/components/ui/alert-dialog";
import { Button } from "@project/components/ui/button";

export type DeleteConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  itemName?: string;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
};

export function DeleteConfirmDialog({
  open,
  onOpenChange,
  title = "Delete record?",
  description = "This action cannot be undone. The record will be permanently removed.",
  itemName,
  loading = false,
  onConfirm,
}: DeleteConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[calc(100vw-2rem)] max-w-md overflow-hidden rounded-2xl border-border bg-background p-0 shadow-xl">
        <div className="bg-destructive/5 flex items-start gap-4 border-b border-border px-5 py-4">
          <div className="bg-destructive/10 grid h-11 w-11 shrink-0 place-items-center rounded-full text-destructive">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <AlertDialogHeader className="space-y-1 text-left">
            <AlertDialogTitle className="text-lg font-bold text-foreground">
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              {description}
            </AlertDialogDescription>
          </AlertDialogHeader>
        </div>

        {itemName && (
          <div className="bg-muted/40 mx-5 mt-4 rounded-xl border border-border px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Record to delete
            </p>
            <p className="mt-1 truncate text-sm font-medium text-foreground">
              {itemName}
            </p>
          </div>
        )}

        <AlertDialogFooter className="gap-2 px-5 py-4 sm:justify-end">
          <AlertDialogCancel
            disabled={loading}
            className="hover:bg-muted/60 rounded-xl border-border bg-background"
          >
            Cancel
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={loading}
            className="gap-2 rounded-xl"
            onClick={() => void onConfirm()}
          >
            <Trash2 className="h-4 w-4" />
            {loading ? "Deleting…" : "Delete"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
